import { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/contexts/AuthContext';
import { ProjectProvider } from '@/contexts/ProjectProvider';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { AppShell } from '@/components/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { LoginPage } from '@/components/auth/LoginPage';
import { useBootReady } from '@/lib/bootReady';
import { queryClient } from '@/lib/queryClient';
import { startPresence } from '@/lib/presence';
import { useAuthStore } from '@/store/auth';
import { useChatwootStore } from '@/store/chatwoot';
import { RealtimeServices } from '@/components/RealtimeServices';
import { ToastHost } from '@/components/ToastHost';
import { BackButtonHandler } from '@/components/BackButtonHandler';
import { PushServices } from '@/components/PushServices';
import InboxPage from '@/pages/InboxPage';
import ConversationPage from '@/pages/ConversationPage';
import PipelinePage from '@/pages/PipelinePage';
import DealPage from '@/pages/DealPage';
import CallsPage from '@/pages/CallsPage';
import CalendarPage from '@/pages/CalendarPage';
import MorePage from '@/pages/MorePage';

/** Побочные службы, которым нужен вошедший человек: отметка «в сети», очистка при выходе. */
function SessionServices() {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const isLoading = useAuthStore((s) => s.isLoading);

  // Отметка присутствия — пока человек вошёл (и приложение на экране).
  useEffect(() => {
    if (!userId) return;
    return startPresence();
  }, [userId]);

  // Выход (или смена человека): чужие данные и пропуск Chatwoot не оставляем.
  useEffect(() => {
    if (isLoading) return;
    if (!userId) {
      useChatwootStore.getState().clear();
      queryClient.clear();
    }
  }, [userId, isLoading]);

  // Если входа нет, заставку гасит LoginPage; если есть — первый кадр оболочки.
  useBootReady(!isLoading && !!userId);
  return null;
}

/** У каждого экрана своя граница сбоя: падение одного не липнет к соседям. */
const экран = (section: string, el: React.ReactNode) => (
  <ErrorBoundary section={section} key={section}>{el}</ErrorBoundary>
);

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <ProjectProvider>
            <SessionServices />
            <RealtimeServices />
            <ToastHost />
            <BackButtonHandler />
            <PushServices />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<AppShell />}>
                  <Route path="/inbox" element={экран('inbox', <InboxPage />)}>
                    {/* Переписка рисуется поверх списка; номер кабинета — в адресе (display_id уникален лишь в кабинете) */}
                    <Route path=":accountId/:displayId" element={экран('conversation', <ConversationPage />)} />
                  </Route>
                  <Route path="/pipeline" element={экран('pipeline', <PipelinePage />)}>
                    {/* Сделка без переписки рисуется поверх воронки (с перепиской — открывается во «Входящих»). */}
                    <Route path="deal/:dealId" element={экран('deal', <DealPage />)} />
                  </Route>
                  <Route path="/calls" element={экран('calls', <CallsPage />)} />
                  <Route path="/calendar" element={экран('calendar', <CalendarPage />)} />
                  <Route path="/more" element={экран('more', <MorePage />)} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/inbox" replace />} />
            </Routes>
          </ProjectProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
